import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useProjectStore from '../store/projectStore';
import useUIStore from '../store/uiStore';
import { formatDateDDMMYY } from '../utils/formatDate';
import { getSchedulePortfolio } from '../services/scheduleService';
import { HEALTH_COLORS, HEALTH_LABELS, HEALTH_ORDER, formatSlip } from '../constants/schedule';

// Monitored = frozen schedule with Weekly monitoring ticked (approved
// 2026-10-25): purple left border + "Monitored" badge, sorted to the top
// worst health first. Green/amber/red stays reserved for health (the PM's
// weekly review), so a late monitored project never reads as "fine".
const isMonitored = (s) => !!s && s.weekly_monitoring && s.schedule_status === 'baselined';
const isKey = (s) => isMonitored(s) && s.is_key;
const MONITOR_FILTERS = [
  { value: 'all', label: 'All', match: () => true },
  { value: 'key', label: 'Key', match: isKey },
  { value: 'monitored', label: 'Monitored', match: isMonitored },
  { value: 'not', label: 'Not monitored', match: (s) => !isMonitored(s) },
];

const STATUS_COLORS = {
  active: 'bg-gray-100 text-gray-800',
  completed: 'bg-green-100 text-green-800',
  on_hold: 'bg-amber-100 text-amber-800',
  cancelled: 'bg-red-100 text-red-800',
};

const ProjectsListPage = () => {
  const { projects, isLoading, error, fetchProjects } = useProjectStore();
  const [statusFilter, setStatusFilter] = useState('active');
  const [monitorFilter, setMonitorFilter] = useState('all');
  const [portfolio, setPortfolio] = useState({});
  const showToast = useUIStore((s) => s.showToast);

  useEffect(() => {
    fetchProjects(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    getSchedulePortfolio()
      .then((res) => setPortfolio(Object.fromEntries(res.data.projects.map((s) => [s.project_id, s]))))
      .catch(() => {}); // the list still works without schedule info
  }, []);

  const counts = Object.fromEntries(MONITOR_FILTERS.map((f) => [f.value, projects.filter((p) => f.match(portfolio[p.id])).length]));
  const activeFilter = MONITOR_FILTERS.find((f) => f.value === monitorFilter);
  const shown = projects
    .filter((p) => activeFilter.match(portfolio[p.id]))
    .map((p, i) => ({ p, s: portfolio[p.id], i }))
    .sort((a, b) => {
      const ma = isMonitored(a.s), mb = isMonitored(b.s);
      if (ma !== mb) return ma ? -1 : 1;
      if (!ma) return a.i - b.i; // unmonitored keep the server's order
      if (isKey(a.s) !== isKey(b.s)) return isKey(a.s) ? -1 : 1;
      const ha = HEALTH_ORDER[a.s.health] ?? 2, hb = HEALTH_ORDER[b.s.health] ?? 2;
      return ha - hb || b.s.handover_slip_days - a.s.handover_slip_days;
    });

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-white">Projects</h1>
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="on_hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
              <option value="all">All</option>
            </select>
            <Link
              to="/projects/create"
              className="bg-komal-blue hover:bg-komal-blue-dark text-white text-sm font-semibold px-4 py-2 rounded"
            >
              + New Project
            </Link>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap mb-4">
          {MONITOR_FILTERS.map((f) => (
            <button key={f.value} onClick={() => setMonitorFilter(f.value)}
              className={`text-xs px-3 py-1.5 rounded-full border ${monitorFilter === f.value
                ? 'bg-komal-blue border-komal-blue text-white' : 'bg-white/90 border-gray-300 text-gray-700'}`}>
              {f.label} ({counts[f.value]})
            </button>
          ))}
        </div>

        {isLoading ? (
          <p className="text-gray-500 text-sm">Loading...</p>
        ) : shown.length === 0 ? (
          <p className="text-gray-500 text-sm">
            {projects.length === 0 && statusFilter === 'active' ? 'No active projects — create one to get started.' : 'No projects match this filter.'}
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {shown.map(({ p, s }) => {
              const monitored = isMonitored(s);
              return (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                className="glass-card p-5 hover:shadow-md transition-shadow"
                // Inline because .glass-card's own border rules win over Tailwind border utilities.
                style={monitored ? { borderLeft: '4px solid #5E1041', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } : undefined}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-semibold text-gray-900">{p.name}</span>
                  {monitored ? (
                    <span className={`text-xs px-2 py-1 rounded font-semibold whitespace-nowrap ${HEALTH_COLORS[s.health] || 'bg-gray-100 text-gray-600'}`}>
                      {s.health ? `${HEALTH_LABELS[s.health]} · ${formatSlip(s.handover_slip_days)}` : 'No review yet'}
                    </span>
                  ) : (
                    <span className={`text-xs px-2 py-1 rounded font-semibold capitalize ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-800'}`}>
                      {p.status?.replace('_', ' ')}
                    </span>
                  )}
                </div>
                {monitored && (
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-komal-blue/10 text-komal-blue font-semibold">Monitored</span>
                    {s.is_key && <span className="text-xs px-2 py-0.5 rounded-full bg-komal-blue text-white font-semibold">Key</span>}
                    <span className="text-xs text-gray-500">Rev {s.current_rev} · handover {formatDateDDMMYY(s.forecast_handover)}</span>
                    <span className={`text-xs ${s.review_status === 'submitted' ? 'text-gray-500' : 'text-red-700'}`}>
                      {s.review_status === 'submitted' ? '· review submitted' : '· review not submitted'}
                    </span>
                  </div>
                )}
                <div className="text-sm text-gray-500">{p.client_name || '—'} · {p.location || '—'}</div>
                {p.parent_project_name && (
                  <div className="text-xs text-komal-blue mt-1">↳ Part of {p.parent_project_name}</div>
                )}
                {p.sub_project_count > 0 && (
                  <div className="text-xs text-komal-blue mt-1">Groups {p.sub_project_count} sub-project{p.sub_project_count === 1 ? '' : 's'}</div>
                )}
                <div className="text-xs text-gray-400 mt-2">
                  Manufacturing due {formatDateDDMMYY(p.manufacturing_expected_completion_date) || '—'} · Site due {formatDateDDMMYY(p.site_installation_expected_completion_date) || '—'}
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsListPage;
